import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Building2, Clock, TrendingDown, Cpu, CheckCircle2, Factory, Zap, Shield, X } from 'lucide-react';

const Home = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Arthaa Build | Precast Concrete Construction Company — Build 50% Faster, Save 20% Cost</title>
        <meta name="description" content="Arthaa Build delivers factory-manufactured precast concrete construction. Build 50% faster, save 20% overall project cost. IS 13920 certified. Serving Punjab, Haryana, Delhi NCR, UP, HP from Chandigarh." />
        <link rel="canonical" href="https://arthaabuild.com/" />
      </Helmet>

      {/* Hero Section */}
      <div 
        className="hero-section" 
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=2070)'
        }}
        data-testid="home-hero-section"
      >
        <div className="hero-overlay"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-20 w-full">
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="max-w-4xl"
          >
            <div className="inline-block px-5 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-6">
              <p className="text-[#7EB3D8] text-sm font-semibold tracking-wide">Building Purpose. Building Prosperity.</p>
            </div>
            <h1 className="font-serif font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-white mb-6" data-testid="hero-headline">
              Build 50% Faster.<br />
              Save 20% Overall Cost.<br />
              <span className="text-[#7EB3D8]">Arthaa Build — The Precast Revolution.</span>
            </h1>
            <p className="text-xl text-white/90 leading-relaxed mb-8 max-w-2xl" data-testid="hero-subheadline">
              At Arthaa Build, factory-manufactured precision meets on-site speed. While traditional construction takes 120+ days, our precast systems deliver in 60-75 days—with superior quality, reduced interest payments, and accelerated revenue generation.
            </p>

            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-4">
                <div className="text-3xl font-bold text-[#7EB3D8] mb-1">50%</div>
                <div className="text-sm text-white/80">Faster Delivery</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-4">
                <div className="text-3xl font-bold text-[#7EB3D8] mb-1">20%</div>
                <div className="text-sm text-white/80">Cost Reduction</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-4">
                <div className="text-3xl font-bold text-[#7EB3D8] mb-1">M25-M40</div>
                <div className="text-sm text-white/80">Grade Concrete</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <Link 
                to="/contact"
                className="inline-block bg-gradient-to-r from-[#2B5278] to-[#1E3A5F] hover:from-[#1E3A5F] hover:to-[#2B5278] text-white font-semibold text-base px-10 py-5 rounded-md transition-all shadow-lg hover:shadow-xl"
                data-testid="hero-cta-button"
              >
                Get Cost Analysis
              </Link>
              <Link 
                to="/construction"
                className="inline-block bg-white/10 backdrop-blur-md border-2 border-white/40 hover:bg-white/20 text-white font-semibold text-base px-10 py-5 rounded-md transition-all"
                data-testid="hero-secondary-cta"
              >
                See How It Works
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Trust Bar */}
      <div className="bg-slate-900 text-white py-6" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
            <span className="flex items-center gap-2">
              <Factory className="w-4 h-4 text-[#7EB3D8]" />
              <span className="font-medium">Chandigarh Manufacturing Facility</span>
            </span>
            <span className="hidden sm:inline text-slate-600">|</span>
            <span>IS 13920 Seismic Certified</span>
            <span className="hidden sm:inline text-slate-600">|</span>
            <span>Punjab · Haryana · Delhi NCR · UP · HP</span>
            <span className="hidden sm:inline text-slate-600">|</span>
            <span>Advanced Precast Technology</span>
          </div>
        </div>
      </div>

      {/* Traditional vs Precast Comparison */}
      <section className="section-padding bg-white" data-testid="comparison-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="text-center mb-16">
            <p className="text-sm uppercase tracking-widest text-[#2B5278] font-semibold mb-4">The Revolutionary Difference</p>
            <h2 className="font-serif font-bold text-4xl sm:text-5xl text-slate-900 mb-6">
              Traditional vs. Precast: The Numbers Speak
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              See why leading developers across India are switching to precast concrete construction.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Traditional Method */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-slate-50 rounded-xl p-8 border-2 border-slate-200"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-slate-200 rounded-lg flex items-center justify-center">
                  <X className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <h3 className="font-serif font-bold text-2xl text-slate-900">Traditional Cast-in-Situ</h3>
                  <p className="text-sm text-slate-500">Conventional Method</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-slate-200">
                  <span className="text-slate-700 font-medium">Construction Time</span>
                  <span className="text-xl font-bold text-slate-900">120-150 days</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-200">
                  <span className="text-slate-700 font-medium">Overall Project Cost</span>
                  <span className="text-slate-900 font-semibold">High</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-200">
                  <span className="text-slate-700 font-medium">Labour Cost</span>
                  <span className="text-slate-600">Higher</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-200">
                  <span className="text-slate-700 font-medium">Quality Control</span>
                  <span className="text-slate-600">Site-dependent</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-200">
                  <span className="text-slate-700 font-medium">Weather Impact</span>
                  <span className="text-slate-600">High delays</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-200">
                  <span className="text-slate-700 font-medium">Interest Payments</span>
                  <span className="text-slate-600">Higher</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-slate-700 font-medium">Material Wastage</span>
                  <span className="text-slate-600">15-20%</span>
                </div>
              </div>
            </motion.div>

            {/* Precast Method */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-[#2B5278]/5 to-[#1E3A5F]/5 rounded-xl p-8 border-2 border-[#2B5278]"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#2B5278] rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-serif font-bold text-2xl text-slate-900">Arthaa Build Precast Concrete</h3>
                  <p className="text-sm text-[#2B5278] font-semibold">Revolutionary Technology</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center py-3 border-b border-[#2B5278]/20">
                  <span className="text-slate-700 font-medium">Construction Time</span>
                  <div className="text-right">
                    <span className="text-xl font-bold text-[#2B5278]">60-75 days</span>
                    <div className="text-xs text-green-600 font-semibold">↓ 50% Faster</div>
                  </div>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-[#2B5278]/20">
                  <span className="text-slate-700 font-medium">Overall Project Cost</span>
                  <div className="text-right">
                    <span className="text-xl font-bold text-[#2B5278]">20% Less</span>
                    <div className="text-xs text-green-600 font-semibold">↓ Significant Savings</div>
                  </div>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-[#2B5278]/20">
                  <span className="text-slate-700 font-medium">Labour Cost</span>
                  <div className="text-right">
                    <span className="text-[#2B5278] font-semibold">Much Lower</span>
                    <div className="text-xs text-slate-600">Less labour intensity</div>
                  </div>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-[#2B5278]/20">
                  <span className="text-slate-700 font-medium">Quality Control</span>
                  <span className="text-[#2B5278] font-semibold">Factory Precision</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-[#2B5278]/20">
                  <span className="text-slate-700 font-medium">Weather Impact</span>
                  <span className="text-[#2B5278] font-semibold">Zero Impact</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-[#2B5278]/20">
                  <span className="text-slate-700 font-medium">Interest Payments</span>
                  <div className="text-right">
                    <span className="text-[#2B5278] font-semibold">Significantly Reduced</span>
                    <div className="text-xs text-slate-600">Faster completion</div>
                  </div>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-[#2B5278]/20">
                  <span className="text-slate-700 font-medium">Revenue Generation</span>
                  <div className="text-right">
                    <span className="text-[#2B5278] font-semibold">Accelerated</span>
                    <div className="text-xs text-slate-600">Quick occupancy</div>
                  </div>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-slate-700 font-medium">Material Wastage</span>
                  <div className="text-right">
                    <span className="text-[#2B5278] font-semibold">&lt;3%</span>
                    <div className="text-xs text-slate-600">Minimal waste</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* ROI Calculator Teaser */}
          <div className="bg-slate-900 rounded-xl p-10 text-white text-center">
            <h3 className="font-serif font-bold text-3xl mb-4">Calculate Your Savings with Arthaa Build</h3>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
              Precast construction saves overall project cost by 20%, significantly reduces interest payments through faster completion, and accelerates revenue generation with quick occupancy.
            </p>
            <Link 
              to="/contact"
              className="inline-block bg-gradient-to-r from-[#2B5278] to-[#1E3A5F] hover:from-[#1E3A5F] hover:to-[#2B5278] text-white font-semibold text-base px-10 py-4 rounded-md transition-all shadow-lg"
            >
              Get Detailed Cost Analysis
            </Link>
          </div>
        </div>
      </section>

      {/* Why Precast is Revolutionary */}
      <section className="section-padding bg-slate-50" data-testid="revolutionary-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="text-center mb-16">
            <h2 className="font-serif font-bold text-4xl sm:text-5xl text-slate-900 mb-6">
              Why Precast is Revolutionary
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              A fundamental shift from traditional construction—engineered for speed, quality, and economics.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="luxury-card bg-white p-8 rounded-xl"
            >
              <div className="w-14 h-14 bg-[#2B5278]/10 rounded-lg flex items-center justify-center mb-6">
                <Factory className="w-7 h-7 text-[#2B5278]" />
              </div>
              <h3 className="font-serif font-bold text-xl text-slate-900 mb-3">Superior Quality</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Factory-controlled environment ensures superior quality with ±2mm tolerances. Steam curing guarantees M25-M40 grade strength every time.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="luxury-card bg-white p-8 rounded-xl"
            >
              <div className="w-14 h-14 bg-[#2B5278]/10 rounded-lg flex items-center justify-center mb-6">
                <Clock className="w-7 h-7 text-[#2B5278]" />
              </div>
              <h3 className="font-serif font-bold text-xl text-slate-900 mb-3">Parallel Execution</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                While site preparation happens, factory production runs simultaneously—cutting months from project timelines.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="luxury-card bg-white p-8 rounded-xl"
            >
              <div className="w-14 h-14 bg-[#2B5278]/10 rounded-lg flex items-center justify-center mb-6">
                <Zap className="w-7 h-7 text-[#2B5278]" />
              </div>
              <h3 className="font-serif font-bold text-xl text-slate-900 mb-3">Weather Independent</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Monsoon delays? Heat curing issues? Not anymore. Factory production eliminates weather-related setbacks entirely.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="luxury-card bg-white p-8 rounded-xl"
            >
              <div className="w-14 h-14 bg-[#2B5278]/10 rounded-lg flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-[#2B5278]" />
              </div>
              <h3 className="font-serif font-bold text-xl text-slate-900 mb-3">Seismic Safety</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                IS 13920 certified with zero residual drift design—critical for North India's seismic zones. Immediate post-earthquake occupancy.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technology Integration */}
      <section className="section-padding bg-white" data-testid="technology-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-sm uppercase tracking-widest text-[#2B5278] font-semibold mb-4">Beyond Construction</p>
              <h2 className="font-serif font-bold text-4xl text-slate-900 mb-6">
                IT-Powered Real Estate Intelligence
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                We're not just building faster—we're building smarter. Our IT solutions and AI-powered systems bring transparency, efficiency, and data-driven decision-making to every project.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-[#2B5278] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-slate-900 mb-1">Real-Time Project Monitoring</div>
                    <div className="text-sm text-slate-600">Surveillance systems and progress tracking dashboards</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-[#2B5278] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-slate-900 mb-1">AI-Powered Facility Management</div>
                    <div className="text-sm text-slate-600">Predictive maintenance and automated asset optimization</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-[#2B5278] flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-slate-900 mb-1">Property Management Consultancy</div>
                    <div className="text-sm text-slate-600">Data analytics for portfolio optimization and ROI maximization</div>
                  </div>
                </li>
              </ul>
              <Link 
                to="/property-tech"
                className="inline-flex items-center gap-2 text-[#2B5278] font-semibold hover:gap-4 transition-all"
              >
                Explore Technology Solutions <span>→</span>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070" 
                alt="Technology Integration"
                className="rounded-xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-lg shadow-xl max-w-xs">
                <Cpu className="w-10 h-10 text-[#2B5278] mb-3" />
                <p className="font-semibold text-slate-900 mb-1">Smart Infrastructure</p>
                <p className="text-sm text-slate-600">Surveillance, IoT sensors, and AI analytics integrated from day one</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Integrated Services */}
      <section className="section-padding bg-slate-900 text-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="text-center mb-16">
            <h2 className="font-serif font-bold text-4xl sm:text-5xl mb-6">
              End-to-End Real Estate Solutions
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Leveraging 10+ years of industry experience with 50+ national and international brand partnerships.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
              <h3 className="font-serif font-bold text-xl mb-4">Construction Services</h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li>• Precast Concrete Systems</li>
                <li>• Façade Engineering</li>
                <li>• MEP Solutions</li>
                <li>• High-Quality Finishing</li>
              </ul>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
              <h3 className="font-serif font-bold text-xl mb-4">Real Estate Consultancy</h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li>• Commercial Leasing</li>
                <li>• Brand Liaison (50+ brands)</li>
                <li>• Investment Advisory</li>
                <li>• Portfolio Development</li>
              </ul>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8">
              <h3 className="font-serif font-bold text-xl mb-4">Technology Solutions</h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li>• Facility Management (AI)</li>
                <li>• Property Management IT</li>
                <li>• Surveillance Systems</li>
                <li>• Data Analytics Platforms</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-gradient-to-br from-[#2B5278] to-[#1E3A5F]" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-20 text-center">
          <h2 className="font-serif font-bold text-4xl sm:text-5xl text-white mb-6">
            Ready to Build 50% Faster with Arthaa Build?
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Get a detailed cost and time comparison for your project. Our team will show you exactly how much you can save with precast concrete technology.
          </p>
          <Link 
            to="/contact"
            className="inline-block bg-white text-slate-900 hover:bg-slate-50 font-semibold text-base px-12 py-5 rounded-md transition-all shadow-lg"
            data-testid="cta-contact-button"
          >
            Get Free Project Analysis
          </Link>
          <p className="text-sm text-white/80 mt-6">Serving Punjab · Haryana · Delhi NCR · UP · HP · Pan-India</p>
        </div>
      </section>
    </div>
  );
};

export default Home;